import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { HeapVideoContentType, HeapVideoOrderData } from "@app/provider/types";
import { useWistiaScript } from "@app/hooks";
import { bindHeapVideo } from "@app/utils/bindHeapVideo";
import { ConfigContext } from "@app/provider";
import { LoadingContext } from "@natera/platform/lib/components/context";
import classNames from "classnames";

import "./videoPlayer.scss";

export enum VideoType {
  SALIVA = "SALIVA",
  HORIZON = "HORIZON",
  PANORAMA = "PANORAMA",
}

const CAPTIONS_PLUGIN_NAME = "captions";

export type CaptionsPlugin = {
  turnOn(): void;
  turnOff(): void;
  setLanguage(code: string): void;
};

export interface IVideo {
  percentWatched(): number;

  unbind(): void;

  bind(arg0: string, arg1: (arg?: unknown) => void): unknown;

  duration(): number;

  requestFullscreen(): void;

  plugin(name: typeof CAPTIONS_PLUGIN_NAME): {
    then(onFulfilled: (plugin: CaptionsPlugin) => void): void;
  };
}

export type CaptionsOptions = {
  captionsEnabled: boolean;
  language?: string;
};

const DEFAULT_CAPTIONS_OPTIONS: CaptionsOptions = { captionsEnabled: false };

const applyCaptionsState = (
  videoRef: React.RefObject<IVideo | null>,
  captionsOptionsRef: React.RefObject<CaptionsOptions>
) => {
  const video$ = videoRef.current;
  if (!video$) {
    return;
  }

  video$.plugin(CAPTIONS_PLUGIN_NAME).then((captions) => {
    if (videoRef.current !== video$) {
      return;
    }

    const { language, captionsEnabled } = captionsOptionsRef.current;

    if (language) {
      captions.setLanguage(language);
    }

    if (captionsEnabled) {
      captions.turnOn();
    } else {
      captions.turnOff();
    }
  });
};

type Props = {
  id: string;
  thumbnailAltText: string;
  heapVideoContentType?: HeapVideoContentType;
  heapVideoOrderData?: HeapVideoOrderData;
  setIsVideoPlayerLoading?: Dispatch<SetStateAction<boolean>>;
  bindNevaHeapVideo?: (video$: IVideo, title: string) => void;
  thumbnail?: string;
  captionsOptions?: CaptionsOptions;
};

const VideoPlayer: React.FC<Props> = ({
  id,
  thumbnailAltText,
  heapVideoContentType,
  heapVideoOrderData,
  setIsVideoPlayerLoading,
  bindNevaHeapVideo,
  thumbnail,
  captionsOptions = DEFAULT_CAPTIONS_OPTIONS,
}) => {
  useWistiaScript();
  const [videoIsLoading, setVideoIsLoading] = useState<boolean>(true);
  const videoRef = useRef<IVideo | null>(null);
  const { config } = useContext(ConfigContext);
  const effectiveCaptionsOptions = config?.wistia?.captionsEnabled
    ? captionsOptions
    : DEFAULT_CAPTIONS_OPTIONS;
  const captionsOptionsRef = useRef(effectiveCaptionsOptions);
  captionsOptionsRef.current = effectiveCaptionsOptions;

  React.useEffect(() => {
    window._wq = window._wq || [];

    const bindObj = {
      id,
      options: {
        thumbnailAltText: thumbnailAltText,
        ...(thumbnail && { stillUrl: thumbnail }),
      },
      onReady: (video$: IVideo) => {
        videoRef.current = video$;
        setVideoIsLoading(false);
        setIsVideoPlayerLoading && setIsVideoPlayerLoading(false);
        applyCaptionsState(videoRef, captionsOptionsRef);
        if (bindNevaHeapVideo) {
          bindNevaHeapVideo(video$, thumbnailAltText);
        } else {
          bindHeapVideo(video$, heapVideoContentType, heapVideoOrderData);
        }
      },
    };

    window._wq.push(bindObj);

    return () => {
      videoRef.current = null;
      window._wq.push({ revoke: bindObj });
    };
  }, [
    heapVideoContentType,
    heapVideoOrderData,
    thumbnailAltText,
    id,
    setVideoIsLoading,
  ]);

  useEffect(() => {
    applyCaptionsState(videoRef, captionsOptionsRef);
  }, [
    effectiveCaptionsOptions.captionsEnabled,
    effectiveCaptionsOptions.language,
  ]);

  React.useEffect(() => {
    return () => {
      window._wq.push({ replaceWith: () => id });
    };
  }, [id]);

  const wistiaId = `wistia_embed wistia_async_${id}`;

  React.useEffect(() => {
    const observedButtons = new WeakSet<Element>();

    const removeTitleAttribute = (button: HTMLElement) => {
      if (button.hasAttribute("title")) {
        button.removeAttribute("title");
      }
    };

    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "title"
        ) {
          removeTitleAttribute(mutation.target as HTMLElement);
        } else if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;

            const buttons = node.matches(".w-vulcan-v2-button")
              ? [node]
              : Array.from(node.querySelectorAll(".w-vulcan-v2-button"));

            buttons.forEach((button) => {
              if (!observedButtons.has(button)) {
                observedButtons.add(button);
                removeTitleAttribute(button as HTMLElement);
                observer.observe(button, {
                  attributes: true,
                  attributeFilter: ["title"],
                });
              }
            });
          });
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="wrap">
      <LoadingContext isLoading={videoIsLoading} />
      <div
        className={classNames("video-wrap", {
          "video-wrap--loading": videoIsLoading,
        })}
      >
        <div className="wistia_responsive_padding">
          <div
            id="wistia_responsive_wrapper"
            className="wistia_responsive_wrapper"
            data-testid="wistia_responsive_wrapper"
          >
            <div key={wistiaId} className={wistiaId} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
